import { type MarkdownRendererOptions, SolidStreamingMarkdownChildren } from "@storytell/markdown";
import type { LinkASTNode } from "@storytell/markdown/src/solid-markdown/toastmark/ast";

const linkClass =
  "text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline underline-offset-2";

// Centralized markdown renderer configuration for consistent styling across chat
// surfaces. Links open in a new tab so the user does not lose the current thread.
// `!` overrides win against the Tailwind Typography `prose` plugin if any wrapper
// still applies it. Adjacent-heading variants keep stacked titles close (h1 → h2
// subtitle) without going flush.
const tightHeading =
  "mb-2! mt-4! first:mt-0! [h1+&]:mt-1! [h2+&]:mt-1! [h3+&]:mt-1! [h4+&]:mt-1! [h5+&]:mt-1! [h6+&]:mt-1!";

// Also dresses tables built outside the markdown renderer so they match GFM
// tables in the same chat. `tableCell` carries no alignment; callers add it.
export const tableClasses = {
  table: "w-full border-collapse rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700",
  tableRow: "border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50",
  tableCell: "px-4 py-3 text-gray-700 dark:text-gray-300",
  tableHead: "bg-gray-50 dark:bg-gray-800 font-semibold text-gray-900 dark:text-gray-100",
  tableBody: "bg-white dark:bg-gray-900",
};

// Provider content inside a tool card reads at the activity's size, one step
// under the answer, so a Notion page or an MCP result never outweighs the
// reply it feeds. Headings step down with it; the rest inherits the wrapper's
// text-sm.
export const compactMarkdownOptions: MarkdownRendererOptions = {
  classes: {
    document: "max-w-none",
    heading: (node) => {
      switch (node.level) {
        case 1:
          return `text-lg font-semibold leading-snug text-gray-900 dark:text-gray-100 ${tightHeading}`;
        case 2:
          return `text-base font-semibold leading-snug text-gray-900 dark:text-gray-100 ${tightHeading}`;
        default:
          return `text-sm font-medium leading-snug text-gray-900 dark:text-gray-100 ${tightHeading}`;
      }
    },
    paragraph: "mb-2! last:mb-0! leading-relaxed text-gray-700 dark:text-gray-300",
    list: (node) => {
      const hasTaskItems = node.children?.some(
        (c) => c.type === "item" && (c as { listData?: { task?: boolean } }).listData?.task
      );
      if (hasTaskItems) return "list-none pl-5 mb-2! leading-relaxed text-gray-700 dark:text-gray-300";
      switch (node.listData?.type) {
        case "ordered":
          return "list-decimal pl-5 mb-2! leading-relaxed text-gray-700 dark:text-gray-300";
        case "bullet":
          return "list-disc pl-5 mb-2! leading-relaxed text-gray-700 dark:text-gray-300";
        default:
          return "mb-2!";
      }
    },
    item: (node) => {
      const isTask = (node as { listData?: { task?: boolean } }).listData?.task;
      return isTask ? "flex items-center gap-2 mb-1! leading-relaxed [&>p]:mb-0!" : "mb-1! leading-relaxed [&>p]:mb-0!";
    },
    code: "bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded-md text-xs font-mono text-gray-800 dark:text-gray-200",
    codeBlock: "mb-2! text-xs border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden",
    blockQuote:
      "border-l-2 border-blue-500 pl-3 py-1 my-2! italic bg-blue-50 dark:bg-blue-900/20 text-gray-700 dark:text-gray-300 leading-relaxed",
    ...tableClasses,
    table: `${tableClasses.table} mb-3`,
    tableCell: "px-3 py-2 text-left text-gray-700 dark:text-gray-300",
    link: linkClass,
    strong: "font-semibold text-gray-900 dark:text-gray-100",
  },
  components: {
    link: (props: { node: LinkASTNode }) => (
      <a href={props.node.destination || "#"} class={linkClass} target="_blank" rel="noopener noreferrer">
        <SolidStreamingMarkdownChildren node={props.node} />
      </a>
    ),
  },
};

export const defaultMarkdownOptions: MarkdownRendererOptions = {
  classes: {
    document: "max-w-none",
    heading: (node) => {
      switch (node.level) {
        case 1:
          return `text-3xl font-bold leading-snug text-gray-900 dark:text-gray-100 ${tightHeading}`;
        case 2:
          return `text-2xl font-semibold leading-snug text-gray-900 dark:text-gray-100 ${tightHeading}`;
        case 3:
          return `text-xl font-medium leading-snug text-gray-900 dark:text-gray-100 ${tightHeading}`;
        case 4:
          return `text-lg font-medium leading-snug text-gray-900 dark:text-gray-100 ${tightHeading}`;
        case 5:
          return `text-base font-medium leading-snug text-gray-900 dark:text-gray-100 ${tightHeading}`;
        case 6:
          return `text-sm font-medium leading-snug text-gray-900 dark:text-gray-100 ${tightHeading}`;
        default:
          return `text-lg font-medium leading-snug text-gray-900 dark:text-gray-100 ${tightHeading}`;
      }
    },
    paragraph: "mb-3! last:mb-0! leading-relaxed text-gray-700 dark:text-gray-300",
    list: (node) => {
      const hasTaskItems = node.children?.some(
        (c) => c.type === "item" && (c as { listData?: { task?: boolean } }).listData?.task
      );
      if (hasTaskItems) {
        return "list-none pl-6 mb-3! leading-relaxed text-gray-700 dark:text-gray-300";
      }
      switch (node.listData?.type) {
        case "ordered":
          return "list-decimal pl-6 mb-3! leading-relaxed text-gray-700 dark:text-gray-300";
        case "bullet":
          return "list-disc pl-6 mb-3! leading-relaxed text-gray-700 dark:text-gray-300";
        default:
          return "mb-3!";
      }
    },
    item: (node) => {
      const isTask = (node as { listData?: { task?: boolean } }).listData?.task;
      return isTask
        ? "flex items-center gap-2 mb-1.5! leading-relaxed [&>p]:mb-0!"
        : "mb-1.5! leading-relaxed [&>p]:mb-0!";
    },
    code: "bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-md text-sm font-mono text-gray-800 dark:text-gray-200",
    codeBlock: "mb-3! border border-gray-200 dark:border-gray-700 shadow-sm rounded-lg overflow-hidden",
    blockQuote:
      "border-l-4 border-blue-500 pl-4 py-2 my-3! italic bg-blue-50 dark:bg-blue-900/20 text-gray-700 dark:text-gray-300 leading-relaxed",
    ...tableClasses,
    table: `${tableClasses.table} mb-4`,
    tableCell: `${tableClasses.tableCell} text-left`,
    link: linkClass,
    strong: "font-semibold text-gray-900 dark:text-gray-100",
  },
  components: {
    link: (props: { node: LinkASTNode }) => (
      <a href={props.node.destination || "#"} class={linkClass} target="_blank" rel="noopener noreferrer">
        <SolidStreamingMarkdownChildren node={props.node} />
      </a>
    ),
  },
};
